import { useEffect, useState } from "react";
import { ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";
import { SearchInputIcon, ResultsLoading } from "@/components/SearchLoading";
import NewBadge from "@/components/NewBadge";
import { api } from "@/App";
import { useBrandLogos, brandLogoFor } from "@/lib/brandLogos";
import { fuelLabel, alientechFuelLabel, ecuTypeLabel } from "@/lib/labels";
import ToolLogo from "@/components/ToolLogo";
import MethodGlyph from "@/components/MethodGlyph";
import VehicleTypeIcon from "@/components/VehicleTypeIcon";
import EcuBrandLogo, { ecuBrandLogoAvailable } from "@/components/EcuBrandLogo";

// Canonical filter option lists — mirror the backend's canon-mapping in
// server.py (_VEHICLE_TYPE_CANON / _FUEL_CANON / _ECU_TYPE_CANON), since the
// 4 providers use different vocab/casing for the same real-world categories
// (e.g. Autotuner "trucks" vs Alientech "Truck"; Autotuner ECU/TCU vs the
// other 3 providers' ECM/TCM).
const SOURCE_OPTIONS = [
  { value: "all", label: "Alle Anbieter" },
  { value: "autotuner", label: "Autotuner" },
  { value: "alientech", label: "Alientech" },
  { value: "magicmotorsport", label: "MagicMotorsport" },
  { value: "dynoecu", label: "Original ECU Dateien" },
];

const VEHICLE_TYPE_OPTIONS = [
  { value: "all", label: "Alle Typen" },
  { value: "car", label: "PKW" },
  { value: "truck", label: "LKW" },
  { value: "moto", label: "Motorrad" },
  { value: "agri", label: "Agrar" },
  { value: "boat", label: "Boot" },
  { value: "bus", label: "Bus" },
  { value: "lcv", label: "Nutzfahrzeug" },
  { value: "other", label: "Sonstige" },
  { value: "atv", label: "ATV" },
  { value: "jetski", label: "Jetski" },
];

const FUEL_OPTIONS = [
  { value: "all", label: "Alle Kraftstoffe" },
  { value: "petrol", label: "Benzin" },
  { value: "diesel", label: "Diesel" },
  { value: "electric", label: "Elektro" },
  { value: "hybrid_diesel", label: "Hybrid (Diesel)" },
  { value: "hybrid_petrol", label: "Hybrid (Benzin)" },
];

const METHOD_OPTIONS = [
  { value: "all", label: "Alle Methoden" },
  { value: "obd", label: "OBD" },
  { value: "bench", label: "Bench" },
  { value: "boot", label: "Boot" },
];

const ECU_TYPE_OPTIONS = [
  { value: "all", label: "Alle Steuergeräte" },
  { value: "ecu", label: "Steuergerät (ECU/ECM)" },
  { value: "tcu", label: "Getriebesteuergerät (TCU/TCM)" },
  { value: "acm", label: "ACM" },
];

const ECU_MAKER_OPTIONS = [
  { value: "all", label: "Alle ECU-Hersteller" },
  "ACDelco",
  "Aisin",
  "Bosch",
  "Continental",
  "Delco",
  "Delphi",
  "Denso",
  "Ford",
  "Getrag",
  "Hitachi",
  "Keihin",
  "Kefico",
  "Marelli",
  "Mitsubishi",
  "Siemens",
  "Temic",
  "Valeo",
  "Visteon",
  "ZF",
].map((value) => (typeof value === "string" ? { value, label: value } : value));

function FacetFilter({
  title,
  testid,
  value,
  onChange,
  options,
  renderIcon,
  counts,
  initialLimit = 8,
}) {
  const [showAll, setShowAll] = useState(false);
  const visibleOptions = showAll ? options : options.slice(0, initialLimit);
  const canShowMore = options.length > initialLimit;

  return (
    <div className="rounded-xl border border-neutral-800 bg-panel p-4">
      <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-3">
        {title}
      </div>
      <ul className="space-y-1.5">
        {visibleOptions.map((o) => {
          const active = value === o.value;
          const count = counts?.[o.value];
          return (
            <li key={o.value} className="flex items-center">
              <button
                type="button"
                onClick={() => onChange(active && o.value !== "all" ? "all" : o.value)}
                aria-pressed={active}
                data-testid={`${testid}-opt-${o.value}`}
                className="flex items-center gap-2 text-sm text-neutral-300 hover:text-neutral-100 cursor-pointer w-full text-left"
              >
                <span
                  className={`h-3.5 w-3.5 shrink-0 rounded border ${
                    active
                      ? "border-red-500 bg-red-500 shadow-[inset_0_0_0_3px_rgba(20,20,20,1)]"
                      : "border-neutral-700 bg-panel"
                  }`}
                />
                {renderIcon?.(o.value, o.label)}
                <span className="flex-1 truncate">{o.label}</span>
                {typeof count === "number" && (
                  <span className="text-[10px] font-mono text-neutral-500 bg-panel border border-neutral-800 rounded px-1.5 py-0.5">
                    {count.toLocaleString("de-DE")}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
      {canShowMore && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-2 text-xs text-red-400 hover:text-red-300 transition"
        >
          {showAll ? "Weniger" : "Mehr"}
        </button>
      )}
    </div>
  );
}

function renderSourceFilterIcon(value) {
  if (value === "all") return null;
  return <ToolLogo source={value} className="h-4 max-w-[96px]" />;
}

function renderVehicleTypeFilterIcon(value) {
  if (value === "all") return null;
  return <VehicleTypeIcon type={value} className="h-5 w-7" />;
}

function renderMethodFilterIcon(value) {
  if (value === "all") return null;
  return <MethodGlyph label={value} className="w-8 h-8" />;
}

function renderEcuBrandFilterLogo(value) {
  return (
    value !== "all" &&
    ecuBrandLogoAvailable(value) && (
      <span className="w-7 h-4 flex items-center justify-center shrink-0">
        <EcuBrandLogo name={value} className="max-h-4 max-w-7 w-auto" />
      </span>
    )
  );
}

// Quick cross-provider overview, deliberately NOT built on react-instantsearch
// (that package expects one homogeneous index schema; here the backend
// already merged 4 differently-shaped indices via /api/search-all, so this
// is just a plain debounced fetch + a normalizer that maps each of the 4
// schemas onto one common display shape). No facets/pagination by design —
// full filtering still lives on the 4 individual provider tabs.

const SOURCE_LABELS = {
  autotuner: "Autotuner",
  alientech: "Alientech",
  magicmotorsport: "MagicMotorsport",
  dynoecu: "Original ECU Dateien",
};

function formatBytes(n) {
  if (!n && n !== 0) return null;
  const units = ["B", "kB", "MB", "GB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(1)} ${units[i]}`;
}

const METHOD_ICONS = {
  OBD: "obd",
  OBD_FULL: "obd",
  BENCH: "bench",
  BOOT: "boot",
  BOOT_GLITCH: "boot",
};

function methodBadges(methods) {
  return (methods || []).filter(Boolean).map((m) => {
    const key = m.toString().toUpperCase();
    return { key: `${key}-${Math.random()}`, label: key, methodLabel: METHOD_ICONS[key] || null };
  });
}

function normalizeHit(hit) {
  const source = hit._source;

  if (source === "autotuner") {
    return {
      source,
      brand: hit.manufacturer,
      model: hit.model,
      sub: [hit.version, hit.year].filter(Boolean).join(" · "),
      engine: hit.engine,
      engineSub: [hit.fuel ? fuelLabel(hit.fuel) : null, hit.power ? `${hit.power} PS` : null]
        .filter(Boolean)
        .join(" · "),
      ecu: hit.ecu_name,
      ecuSub: hit.protocol_label || "",
      typeBadge: ecuTypeLabel(hit.ecu_type),
      methods: methodBadges((hit.methods || []).map((m) => m.toUpperCase())),
      href: hit.compat_id ? `/vehicle/${encodeURIComponent(hit.compat_id)}` : null,
    };
  }

  if (source === "alientech") {
    const connections = hit.connections || {};
    const methods = Object.entries(connections)
      .filter(([, v]) => v?.available)
      .map(([k]) => k.toUpperCase());
    return {
      source,
      brand: hit.brand,
      model: hit.model,
      sub: [hit.version, hit.model_year].filter(Boolean).join(" · "),
      engine: hit.engine_code,
      engineSub: [hit.fuel ? alientechFuelLabel(hit.fuel) : null, hit.power_kw ? `${hit.power_kw} kW` : null]
        .filter(Boolean)
        .join(" · "),
      ecu: hit.ecu_full_name,
      ecuSub: hit.ecu_microprocessor || "",
      typeBadge: ecuTypeLabel(hit.ecu_type),
      methods: methodBadges(methods),
      href: hit.permalink || null,
    };
  }

  if (source === "magicmotorsport") {
    const modes = hit.working_modes || {};
    const methods = Object.entries(modes)
      .filter(([, v]) => v?.available)
      .map(([k]) => k.toUpperCase());
    return {
      source,
      brand: hit.brand,
      model: hit.model,
      sub: [hit.version, [hit.produced_from_year, hit.produced_to_year].filter(Boolean).join("–")]
        .filter(Boolean)
        .join(" · "),
      engine: hit.engine_code,
      engineSub: [
        hit.fuel ? alientechFuelLabel(hit.fuel) : null,
        hit.engine_power_kw ? `${hit.engine_power_kw} kW` : null,
      ]
        .filter(Boolean)
        .join(" · "),
      ecu: hit.ecu_name,
      ecuSub: hit.ecu_maker || "",
      typeBadge: ecuTypeLabel(hit.ecu_type),
      methods: methodBadges(methods),
      href: null,
    };
  }

  if (source === "dynoecu") {
    // Fundamentally different schema: one hit = one original ECU dump file,
    // not a vehicle-to-ECU compatibility row — no fuel/vehicle_type/methods
    // concept here. "methods" is repurposed to show which tool can read this
    // specific file (read_hardware), as a plain text badge (no icon).
    return {
      source,
      brand: hit.brand,
      model: hit.model,
      sub: hit.hardware || "",
      engine: hit.engine,
      engineSub: [hit.software ? `SW ${hit.software}` : null, formatBytes(hit.size)]
        .filter(Boolean)
        .join(" · "),
      ecu: hit.ecu_type,
      ecuSub: hit.ecu_maker || "",
      typeBadge: null,
      methods: hit.read_hardware ? [{ key: "read_hardware", label: hit.read_hardware, Icon: null }] : [],
      href: hit.source_url || null,
    };
  }

  return null;
}

function SourceBadge({ source }) {
  return <ToolLogo source={source} className="h-4" />;
}

function Row({ hit, brandLogos }) {
  const row = normalizeHit(hit);
  if (!row) return null;
  const logo = brandLogoFor(brandLogos, row.brand);

  const content = (
    <div className="group grid grid-cols-1 md:grid-cols-12 gap-4 px-4 py-4 w-full transition hover:bg-neutral-800/30">
      <div className="md:col-span-3 flex items-center gap-3">
        {logo && (
          <img
            src={logo}
            alt={row.brand}
            className="h-12 w-12 object-contain rounded bg-neutral-900 border border-neutral-800 p-1 shrink-0"
            loading="lazy"
          />
        )}
        <div>
          <div className="font-medium text-neutral-100">{row.brand || "—"}</div>
          <div className="mt-1">
            <SourceBadge source={row.source} />
          </div>
        </div>
      </div>
      <div className="md:col-span-2">
        <div className="text-neutral-100">{row.model || "—"}</div>
        <div className="text-xs text-neutral-500">{row.sub || "—"}</div>
        <NewBadge firstSeen={hit.first_seen} className="mt-1" />
      </div>
      <div className="md:col-span-2 text-neutral-200">
        {row.engine || "—"}
        <div className="text-[11px] uppercase tracking-widest text-neutral-500 mt-0.5">
          {row.engineSub}
        </div>
      </div>
      <div className="md:col-span-2">
        <div className="flex items-center gap-1 text-neutral-100">
          {row.ecu || "—"}
          {row.href && (
            <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition" />
          )}
        </div>
        <div className="text-[11px] text-neutral-400 mt-0.5">{row.ecuSub}</div>
      </div>
      <div className="md:col-span-1 flex items-start">
        {row.typeBadge && (
          <span className="inline-flex border border-neutral-700 text-neutral-300 bg-panel text-[10px] font-mono rounded px-2 py-0.5">
            {row.typeBadge}
          </span>
        )}
      </div>
      <div className="md:col-span-2 flex flex-wrap gap-1.5 items-start">
        {row.methods.map(({ key, label, methodLabel }) =>
          methodLabel ? (
            <span
              key={key}
              title={label}
              className="inline-flex items-center justify-center"
            >
              <MethodGlyph label={methodLabel} className="w-8 h-8" />
            </span>
          ) : (
            <span
              key={key}
              className="inline-flex border border-neutral-700 text-neutral-300 bg-panel text-[9px] font-mono rounded px-1.5 py-0.5"
            >
              {label}
            </span>
          ),
        )}
      </div>
    </div>
  );

  if (row.href) {
    return (
      <a href={row.href} target="_blank" rel="noreferrer" className="block w-full">
        {content}
      </a>
    );
  }
  return content;
}

export default function UnifiedSearch({ className = "" }) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState("all");
  const [fuelFilter, setFuelFilter] = useState("all");
  const [methodFilter, setMethodFilter] = useState("all");
  const [ecuTypeFilter, setEcuTypeFilter] = useState("all");
  const [ecuMakerFilter, setEcuMakerFilter] = useState("all");
  const [hits, setHits] = useState([]);
  const [bySource, setBySource] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const pageSize = 20;

  const brandLogos = useBrandLogos();

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    setPage(1);
  }, [debounced, sourceFilter, vehicleTypeFilter, fuelFilter, methodFilter, ecuTypeFilter, ecuMakerFilter]);

  const hasFilter =
    sourceFilter !== "all" ||
    vehicleTypeFilter !== "all" ||
    fuelFilter !== "all" ||
    methodFilter !== "all" ||
    ecuTypeFilter !== "all" ||
    ecuMakerFilter !== "all";

  useEffect(() => {
    if (!debounced && !hasFilter) {
      setHits([]);
      setBySource(null);
      setTotal(0);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .get("/search-all", {
        params: {
          q: debounced,
          limit: pageSize,
          page,
          sources: sourceFilter !== "all" ? sourceFilter : undefined,
          vehicle_type: vehicleTypeFilter !== "all" ? vehicleTypeFilter : undefined,
          fuel: fuelFilter !== "all" ? fuelFilter : undefined,
          method: methodFilter !== "all" ? methodFilter : undefined,
          ecu_type: ecuTypeFilter !== "all" ? ecuTypeFilter : undefined,
          ecu_maker: ecuMakerFilter !== "all" ? ecuMakerFilter : undefined,
        },
      })
      .then(({ data }) => {
        if (cancelled) return;
        setHits(data.hits || []);
        setBySource(data.by_source || {});
        setTotal(data.total || 0);
      })
      .catch(() => {
        if (cancelled) return;
        setHits([]);
        setBySource(null);
        setTotal(0);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced, sourceFilter, vehicleTypeFilter, fuelFilter, methodFilter, ecuTypeFilter, ecuMakerFilter, hasFilter, page]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  // Also true during the debounce window, so the indicator appears on the
  // first keystroke rather than only once the request is actually sent.
  const pending = loading || query.trim() !== debounced;
  const active = Boolean(query.trim()) || hasFilter;

  return (
    <div className={`text-neutral-100 ${className}`}>
      <div className="rounded-xl border border-neutral-800 bg-panel p-4">
        <div className="relative flex-1 min-w-[240px]">
          <SearchInputIcon loading={pending && active} />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Suche über alle Anbieter…"
            data-testid="unified-search-input"
            className="appearance-none [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden flex h-9 w-full rounded-md border border-neutral-800 bg-panel px-3 py-1 pl-9 text-base shadow-sm transition-colors placeholder:text-neutral-500 focus:outline-none focus:ring-1 focus:ring-red-500 md:text-sm text-neutral-100"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 mt-6">
        <aside className="space-y-5">
          <FacetFilter
            title="Anbieter"
            testid="unified-filter-source"
            value={sourceFilter}
            onChange={setSourceFilter}
            options={SOURCE_OPTIONS}
            renderIcon={renderSourceFilterIcon}
            counts={bySource || undefined}
          />
          <FacetFilter
            title="Fahrzeugtyp"
            testid="unified-filter-vehicle-type"
            value={vehicleTypeFilter}
            onChange={setVehicleTypeFilter}
            options={VEHICLE_TYPE_OPTIONS}
            renderIcon={renderVehicleTypeFilterIcon}
          />
          <FacetFilter
            title="Kraftstoff"
            testid="unified-filter-fuel"
            value={fuelFilter}
            onChange={setFuelFilter}
            options={FUEL_OPTIONS}
          />
          <FacetFilter
            title="Methoden"
            testid="unified-filter-method"
            value={methodFilter}
            onChange={setMethodFilter}
            options={METHOD_OPTIONS}
            renderIcon={renderMethodFilterIcon}
          />
          <FacetFilter
            title="ECU-Typ"
            testid="unified-filter-ecu-type"
            value={ecuTypeFilter}
            onChange={setEcuTypeFilter}
            options={ECU_TYPE_OPTIONS}
          />
          <FacetFilter
            title="ECU-Hersteller"
            testid="unified-filter-ecu-maker"
            value={ecuMakerFilter}
            onChange={setEcuMakerFilter}
            options={ECU_MAKER_OPTIONS}
            renderIcon={renderEcuBrandFilterLogo}
          />

        {bySource && (
          <div className="rounded-xl border border-neutral-800 bg-panel p-4 space-y-2 text-xs text-neutral-500">
            <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-3">
              Treffer nach Anbieter
            </div>
            {Object.entries(bySource).map(([source, count]) => (
              <div
                key={source}
                className="flex items-center justify-between gap-2"
              >
                <SourceBadge source={source} />
                <span>{count.toLocaleString("de-DE")}</span>
              </div>
            ))}
          </div>
        )}
        </aside>

        <div className="space-y-4 min-w-0">
          {active && !(pending && hits.length === 0) && (
            <div className="text-xs text-neutral-500">
              {pending ? "Suche läuft…" : `${total.toLocaleString("de-DE")} Treffer`}
            </div>
          )}

        {!active && (
          <div className="text-sm text-neutral-500 py-8 text-center">
            Tippe oder wähle einen Filter, um über Autotuner, Alientech,
            MagicMotorsport und Original ECU Dateien gleichzeitig zu suchen.
          </div>
        )}

        {active && !pending && hits.length === 0 && (
          <div className="text-sm text-neutral-500 py-8 text-center">
            {debounced ? `Keine Treffer für "${debounced}".` : "Keine Treffer für diese Filter."}
          </div>
        )}

        {active && (pending || hits.length > 0) && (
          <ResultsLoading loading={pending} hasResults={hits.length > 0}>
            <div className="divide-y divide-neutral-800/70 border border-neutral-800/70 rounded-xl overflow-hidden bg-panel">
              {hits.map((hit) => (
                <Row key={`${hit._source}-${hit.id}`} hit={hit} brandLogos={brandLogos} />
              ))}
            </div>
            <div className="flex items-center justify-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm border border-neutral-800 bg-panel text-neutral-300 hover:text-neutral-50 hover:border-neutral-700 transition disabled:opacity-40 disabled:pointer-events-none"
              >
                <ChevronLeft className="w-4 h-4" /> Zurück
              </button>
              <span className="text-sm text-neutral-400">
                Seite <span className="text-neutral-100">{page}</span> /{" "}
                <span className="text-neutral-100">{totalPages.toLocaleString("de-DE")}</span>
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm border border-neutral-800 bg-panel text-neutral-300 hover:text-neutral-50 hover:border-neutral-700 transition disabled:opacity-40 disabled:pointer-events-none"
              >
                Weiter <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </ResultsLoading>
        )}
        </div>
      </div>
    </div>
  );
}
